import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ApiError } from '@/api/problemDetails'

import {
  completeTask,
  createTask,
  dismissSuggestion,
  dismissTask,
  getTriageOverview,
  getTriageSettings,
  listTasks,
  runTriageNow,
  sendDigestNow,
  setContactImportant,
  setTriageExclusion,
  updateTriageSettings,
} from './api'
import type { AssistantTaskStatus, TriageSettingsInput } from './types'

/** Mesmo padrão de polling do Desk: a visão de Pendências se renova sozinha enquanto está aberta. */
const OverviewPollMs = 30_000

export function triageOverviewQueryOptions(tenantId: string) {
  return queryOptions({
    queryKey: ['triage', tenantId, 'overview'] as const,
    queryFn: () => getTriageOverview(tenantId),
    staleTime: 15_000,
    refetchInterval: OverviewPollMs,
  })
}

export function useTriageOverview(tenantId: string | undefined) {
  return useQuery({
    ...triageOverviewQueryOptions(tenantId ?? ''),
    enabled: tenantId !== undefined,
  })
}

export function useTriageTasks(tenantId: string | undefined, status: AssistantTaskStatus) {
  return useQuery({
    queryKey: ['triage', tenantId ?? '', 'tasks', status] as const,
    queryFn: () => listTasks(tenantId ?? '', status),
    enabled: tenantId !== undefined,
    staleTime: 15_000,
  })
}

function useInvalidateTriage(tenantId: string) {
  const queryClient = useQueryClient()
  return async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['triage', tenantId] })
  }
}

/** Ações do dono sobre uma conversa em Pendências; a visão (e o Desk) revalidam ao concluir. */
export function useTriageConversationActions(tenantId: string) {
  const invalidate = useInvalidateTriage(tenantId)
  const queryClient = useQueryClient()

  const dismiss = useMutation({
    mutationFn: (conversationSeq: number) => dismissSuggestion(tenantId, conversationSeq),
    onSuccess: invalidate,
  })

  const exclusion = useMutation({
    mutationFn: (input: { readonly conversationSeq: number; readonly excluded: boolean }) =>
      setTriageExclusion(tenantId, input.conversationSeq, input.excluded),
    onSuccess: async () => {
      await invalidate()
      await queryClient.invalidateQueries({ queryKey: ['desk', tenantId] })
    },
  })

  const important = useMutation({
    mutationFn: (input: { readonly contactId: string; readonly important: boolean }) =>
      setContactImportant(tenantId, input.contactId, input.important),
    onSuccess: async () => {
      await invalidate()
      await queryClient.invalidateQueries({ queryKey: ['desk', tenantId] })
    },
  })

  const runNow = useMutation({
    mutationFn: (conversationSeq: number) => runTriageNow(tenantId, conversationSeq),
  })

  return { dismiss, exclusion, important, runNow }
}

export function useTriageTaskActions(tenantId: string) {
  const invalidate = useInvalidateTriage(tenantId)

  const create = useMutation({
    mutationFn: (input: {
      readonly title: string
      readonly details?: string | null
      readonly conversationSeq?: number | null
    }) => createTask(tenantId, input),
    onSuccess: invalidate,
  })

  const complete = useMutation({
    mutationFn: (taskId: string) => completeTask(tenantId, taskId),
    onSuccess: invalidate,
  })

  const dismiss = useMutation({
    mutationFn: (taskId: string) => dismissTask(tenantId, taskId),
    onSuccess: invalidate,
  })

  return { create, complete, dismiss }
}

function triageSettingsQueryKey(tenantId: string) {
  return ['triage', tenantId, 'settings'] as const
}

export function useTriageSettings(tenantId: string) {
  return useQuery({
    queryKey: triageSettingsQueryKey(tenantId),
    queryFn: () => getTriageSettings(tenantId),
    retry: (failureCount, error) =>
      !(error instanceof ApiError && error.problem.status === 403) && failureCount < 2,
  })
}

export function useUpdateTriageSettings(tenantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: TriageSettingsInput) => updateTriageSettings(tenantId, input),
    onSuccess: async (settings) => {
      queryClient.setQueryData(triageSettingsQueryKey(tenantId), settings)
      // Ligar/desligar muda o estado vazio de Pendências.
      await queryClient.invalidateQueries({ queryKey: ['triage', tenantId, 'overview'] })
    },
  })
}

/** "Enviar resumo agora": o servidor grava o resultado (ou o motivo) na configuração, então ela é recarregada. */
export function useSendDigestNow(tenantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => sendDigestNow(tenantId),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: triageSettingsQueryKey(tenantId) })
    },
  })
}
